import React, { useState, useCallback, useEffect } from 'react';
import TerminalLoader from './components/TerminalLoader';
import SpaceScene from './components/SpaceScene';
import HUDOverlay from './components/HUDOverlay';
import ProbeResultsModal from './components/ProbeResultsModal';
import DisconnectedScreen from './components/DisconnectedScreen';
import RotateNotification from './components/RotateNotification';
import './App.css';

function App() {
  const [connected, setConnected] = useState(false);
  const [disconnected, setDisconnected] = useState(false);
  const [probeResultsOpen, setProbeResultsOpen] = useState(false);
  const [currentProbeData, setCurrentProbeData] = useState(null);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [launchStatus, setLaunchStatus] = useState('STANDBY');
  const [isTargeting, setIsTargeting] = useState(false);
  const [targetingStartTime, setTargetingStartTime] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect ACTUAL mobile devices (not just narrow windows)
  useEffect(() => {
    const checkMobile = () => {
      // Check if it's actually a touch device (real mobile/tablet)
      const isTouchDevice = (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0
      );
      
      const isSmallScreen = window.matchMedia('(max-width: 768px)').matches;
      
      // Only treat as mobile if BOTH touch-capable AND small screen
      const actuallyMobile = isTouchDevice && isSmallScreen;
      
      setIsMobile(actuallyMobile);
      
      console.log(`📱 Touch Device: ${isTouchDevice}, Small Screen: ${isSmallScreen}, Actually Mobile: ${actuallyMobile}`);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const handleConnect = useCallback(() => {
    setConnected(true);
    setDisconnected(false);
    console.log('🛰️ Satellite connection established!');
  }, []);

  const handleDisconnect = useCallback(() => {
    setConnected(false);
    setDisconnected(true);
    setProbeResultsOpen(false);
    setCurrentProbeData(null);
    setLaunchStatus('STANDBY');
    setIsTargeting(false);
    setTargetingStartTime(null);
    console.log('🔌 Disconnected from satellite - showing termination screen');
  }, []);

  const handleReconnect = useCallback(() => {
    setDisconnected(false);
    setConnected(false);
    setProbeResultsOpen(false);
    setCurrentProbeData(null);
    setLaunchStatus('STANDBY');
    setIsTargeting(false);
    setTargetingStartTime(null);
    console.log('🔄 Reconnecting - returning to terminal loader');
  }, []);

  // Handle probe event completion from SpaceScene
  const handleProbeEvent = useCallback((probeData) => {
    console.log('Probe event completed:', probeData);
    setCurrentProbeData(probeData);
    setProbeResultsOpen(true);
    setIsTargeting(false);
    setTimeout(() => {
      setLaunchStatus('STANDBY');
    }, 3000);
  }, []);

  // Handle probe launch (when Moon 1 is clicked)
  const handleProbeLaunch = useCallback(() => {
    console.log('🚀 Probe launch detected - setting status to ACTIVE');
    setLaunchStatus('ACTIVE');
    setIsTargeting(true);
    setTargetingStartTime(Date.now());
  }, []);

  // Handle closing probe results modal
  const handleCloseProbeResults = useCallback(() => {
    setProbeResultsOpen(false);
    setCurrentProbeData(null);
  }, []);

  // Handle audio toggle
  const handleToggleAudio = useCallback(() => {
    setIsAudioMuted(prev => !prev);
  }, []);

  // Show disconnected screen if user disconnected
  if (disconnected) {
    return <DisconnectedScreen onReconnect={handleReconnect} />;
  }

  // Show terminal loader initially
  if (!connected) {
    return <TerminalLoader onConnect={handleConnect} />;
  }

  // Show Spline 3D scene after connection
  return (
    <div 
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden'
      }}
      className={isMobile ? 'mobile-view' : 'desktop-view'}
    >
      <RotateNotification />
      <SpaceScene 
        onProbeEvent={handleProbeEvent}
        onProbeLaunch={handleProbeLaunch}
        isAudioMuted={isAudioMuted}
      />
      <HUDOverlay 
        onDisconnect={handleDisconnect} 
        onViewProbeResults={handleProbeEvent}
        onToggleAudio={handleToggleAudio}
        isAudioMuted={isAudioMuted}
        launchStatus={launchStatus}
        isTargeting={isTargeting}
        targetingStartTime={targetingStartTime}
        isMobile={isMobile}
      />
      <ProbeResultsModal 
        open={probeResultsOpen}
        onClose={handleCloseProbeResults}
        probeData={currentProbeData}
      />
    </div>
  );
}

export default App;
